/* eslint-disable no-confusing-arrow */
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Checkbox, Loading, Modal, RadioTile, Separator, toast, Tooltip, Typography } from '@notch-ordering/ui-components';
import { useInvoiceStore } from '@ar/stores/InvoicesStore';
import { useSupplierStore } from '@ar/stores/SupplierStore';
import InfoIcon from '@icons/info-icon.svg';
import InfoOutlineIcon from '@icons/info-outline-icon.svg';
import BigNumber from 'bignumber.js';
import { GetPaymentMethodResponse, getPaymentMethods,
    payInvoices,
    Transaction } from '@ar/network/AccountsReceivable.network';
import { FETCH_TRANSACTIONS_QUERY_KEY } from '@ar/hooks/queries/InvoicesQueries.hook';

import { mergeClassNames } from '@v2/utils/Helpers';
import { areAllInvoicesFromSameCustomer,
    calculateSurchargeAfterCredits,
    FETCH_BULK_CUSTOMERS_PAYMENT_METHODS_QUERY_KEY,
    groupInvoicesByCustomerID,
    PaymentMethodByCustomer } from '@ar/components/Invoices/Modals/BulkManualChargeModal/BulkManualChargeModalConstants';
import { getGpoId } from '@v2/utils/GPOUtils';
import { LoadingPaymentMethodBanner } from '@ar/components/Invoices/Modals/BulkManualChargeModal/LoadingPaymentMethodBanner';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getCurrencyFormat } from '@v2/utils/CurrencyUtils';
import { isSurchargeEnabled } from '@v2/utils/Surcharge';
import { isPendingMandatePaymentMethod } from '@v2/utils/PaymentMethodUtils';
import { FETCH_NOT_APPLIED_CREDIT_MEMO_QUERY_KEY, useGetNotAppliedCreditMemos } from '@ar/hooks/queries/CreditQueries.hook';
import { centsToDollars, formatAsCurrency } from '@/utils/Formatters';
import { AmplitudeCharge, AmplitudeEventActions, useAmplitude } from '@/containers/app/AmplitudeContext';
import { useIntegrationSetupStatus } from '@/ar/hooks/queries/IntegrationsQuery.hook';
import { RutterSetupNotCompleteBanner } from '@/ar/components/shared/RutterSetupNotCompleteBanner';
import PaymentMethodBadge from '@/ar/components/shared/PaymentMethodBadge/PaymentMethodBadge';
import { CreditInput } from '../CreditInput';

export const BulkManualChargeModal : React.FC = () => {
    const { modals, setModal, getReadyToBeChargedInvoices, clearSelectedInvoices } = useInvoiceStore();
    const { supplierLoginData } = useSupplierStore();
    const gpoId = getGpoId(supplierLoginData);
    const queryClient = useQueryClient();
    const { isIntegrationSetupNotComplete } = useIntegrationSetupStatus(supplierLoginData.supplier_id);
    const invoices = getReadyToBeChargedInvoices();// check if all invoices to be charged have the same customer id
    const isSameCustomerID = areAllInvoicesFromSameCustomer(invoices);
    const invoicesByCustomerID = groupInvoicesByCustomerID(invoices);
    const customerInvoices = Object.values(invoicesByCustomerID) ?? [];
    const customerIDs = Object.keys(invoicesByCustomerID);
    const flags = useFlags();

    const isUsingSurcharges = isSurchargeEnabled(invoices ?? [], flags, supplierLoginData?.isSupplier, supplierLoginData?.gpo);

    // get the payment methods for each customer

    const paymentMethodQueries = useQueries({
        queries: customerIDs.map((customerID) => ({
            queryKey: [FETCH_BULK_CUSTOMERS_PAYMENT_METHODS_QUERY_KEY, customerID],
            enabled: modals.bulkManualCharge && !!customerID && (!!supplierLoginData?.supplier_id || !!gpoId),
            queryFn: async ():Promise<PaymentMethodByCustomer> => {
                const paymentMethods = await getPaymentMethods({
                    supplierID: supplierLoginData?.supplier_id,
                    gpoId,
                    customerID,
                });
                return {
                    [customerID]: paymentMethods,
                };
            },

        })),

    });
    // check if any of the queries are loading
    const arePaymentMethodsLoading = paymentMethodQueries.some((query) => query.isLoading);
    // set the payment methods by customer
    const paymentMethodsByCustomer:PaymentMethodByCustomer = paymentMethodQueries.reduce((acc, query) => ({
        ...acc,
        ...(query.data || {}),
    }), {});

    // get the invoices that are ready to be charged based on conditions defined in the store
    const getInvoicesWithPaymentMethod = ():Transaction[] => invoices?.filter((inv) => paymentMethodsByCustomer[inv.customer_id]?.payment_methods.length > 0) ?? [];

    // get the total amount to be charged
    const totalOutstandingAmount = invoices.reduce((acc, invoice) => acc + invoice.outstanding_amount, 0);

    const creditQueries = useGetNotAppliedCreditMemos(
        supplierLoginData?.supplier_id,
        customerIDs,
    );

    const areCreditsLoading = creditQueries.some((query) => query.isLoading);

    const [supplierCredits, setSupplierCredits] = useState<Record<string, { amount: number, inputValue: string }>>({});
    const totalCreditToApply = useMemo(() => Object.values(supplierCredits).reduce((sum, credit) => sum + credit.amount, 0), [supplierCredits]);

    // state variables
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<GetPaymentMethodResponse>(null);
    const amount = useMemo(() => centsToDollars(totalOutstandingAmount ?? 0), [totalOutstandingAmount, invoices?.length]);
    const [isLump, setIsLump] = useState(true);
    const [showSurchargeTooltip, setShowSurchargeTooltip] = useState(false);
    const estimatedSurcharges = useMemo(() => {
        if (!isUsingSurcharges) return new BigNumber(0);
        const amountAfterCredits = Math.max(0, amount - totalCreditToApply);
        return calculateSurchargeAfterCredits(
            selectedPaymentMethod,
            invoices.map((invoice) => invoice.estimated_surcharges),
            amount,
            amountAfterCredits
        );
    }, [isUsingSurcharges, selectedPaymentMethod, invoices, amount, totalCreditToApply]);
    const total = useMemo(() => {
        const amountAfterCredits = Math.max(0, amount - totalCreditToApply);
        return isUsingSurcharges ? estimatedSurcharges.plus(amountAfterCredits) : new BigNumber(amountAfterCredits);
    }, [isUsingSurcharges, estimatedSurcharges, totalCreditToApply, amount]);
    const { trackAmplitudeEvent } = useAmplitude();

    const hasIncompletePaymentMethod = selectedPaymentMethod && !selectedPaymentMethod?.last4;

    // close the modal method cleaning up the state and invalidating the query
    const handleOnClose = ():void => {
        setSelectedPaymentMethod(null);
        setIsLump(true);
        setSupplierCredits({});
        setModal('bulkManualCharge', false, null);
        queryClient.invalidateQueries([FETCH_TRANSACTIONS_QUERY_KEY]);
        queryClient.invalidateQueries([FETCH_NOT_APPLIED_CREDIT_MEMO_QUERY_KEY]);
    };

    // mutation to charge the invoices
    const payInvoicesMutation = useMutation(payInvoices, {
        onSuccess: (_data, variables) => {
            const chargeSuccessful = _data.filter((data) => data.charged === true);
            const chargeSuccessfulCount = chargeSuccessful.length;
            const numberOfInvoicesCharged = variables.body.transaction_ids.length;
            let toastMessage;
            if (chargeSuccessfulCount === numberOfInvoicesCharged) {
            // All Invoices charged successfully
                const messageText = `${getCurrencyFormat(total)} charged for ${numberOfInvoicesCharged > 1 ? `${numberOfInvoicesCharged} invoices` : `${numberOfInvoicesCharged} invoice`}`;
                toastMessage = isSameCustomerID ? messageText : `${numberOfInvoicesCharged > 1 ? `${numberOfInvoicesCharged} invoices` : `${numberOfInvoicesCharged} invoice`} charged`;
            } else if (chargeSuccessfulCount === 0) {
            // No Invoices charged successfully
                toastMessage = `${numberOfInvoicesCharged > 1 ? 'All charges have failed' : 'Charge has failed'}`;
            } else if ((chargeSuccessfulCount > 0) && (chargeSuccessfulCount !== numberOfInvoicesCharged)) {
            // Only some Invocies charged successfully
                const textNumberFailed = `${numberOfInvoicesCharged - chargeSuccessfulCount > 1 ? `${numberOfInvoicesCharged - chargeSuccessfulCount} charges have failed` : `${numberOfInvoicesCharged - chargeSuccessfulCount} charge has failed`}`;
                const textNumberSuccesful = `${chargeSuccessfulCount > 1 ? `${chargeSuccessfulCount} charges have succeeded` : `${chargeSuccessfulCount} charges has succeeded`}`;
                toastMessage = `${textNumberFailed}, ${textNumberSuccesful}`;
            }
            toast.show({
                message: toastMessage
            });
            trackAmplitudeEvent(AmplitudeEventActions.charged, { type: AmplitudeCharge.bulkCharge, countInvoices: invoices.length });
            handleOnClose();
            // after the invoices have been charged, clear the selected invoices
            clearSelectedInvoices();
        },
        onError: (error) => {
            toast.show({
                message: 'Something went wrong while charging the invoices',
            });
            // eslint-disable-next-line no-console
            console.error(error);
        },
    });

    const handleCharge = ():void => {
        // get the ids of the invoices to be charged
        const invoicesIDs = getInvoicesWithPaymentMethod().map((invoice) => invoice.id);
        // if there is more than one invoice, use the user selection to determine if it should be lumped else don't lump
        const isLumped = invoices.length > 1 ? isLump : false;

        const customerCreditsArray = Object.entries(supplierCredits)
            .filter(([, credit]) => credit.amount > 0)
            .map(([customerId, credit]) => ({
                customer_id: customerId,
                credit_amount: Math.round(credit.amount * 100), // Convert to cents
            }));

        trackAmplitudeEvent(AmplitudeEventActions.pushCharge, { type: AmplitudeCharge.bulkCharge, countInvoices: invoices.length });
        payInvoicesMutation.mutate({
            supplierID: supplierLoginData?.supplier_id,
            gpoId,
            body: {
                is_lumped: isLumped,
                payment_method_id: selectedPaymentMethod?.id ?? undefined,
                transaction_ids: invoicesIDs,
                customer_credits: customerCreditsArray.length > 0 ? customerCreditsArray : undefined,
            },
        });
    };

    useEffect(() => {
        if (modals.bulkManualCharge) {
            trackAmplitudeEvent(AmplitudeEventActions.pageChargeViewed, { type: AmplitudeCharge.bulkCharge, countInvoices: invoices.length });
        }
    }, [modals.bulkManualCharge]);

    // Initialize credit inputs for each customer when modal opens and credit data is loaded
    useEffect(() => {
        if (modals.bulkManualCharge && !areCreditsLoading && creditQueries.length > 0) {
            const initialCredits: Record<string, { amount: number, inputValue: string }> = {};

            creditQueries.forEach((query) => {
                if (query.data && query.data.availableCredits > 0) {
                    const { customerId } = query.data;
                    const invoicesForCustomer = invoicesByCustomerID[customerId] || [];
                    const customerOutstanding = centsToDollars(
                        invoicesForCustomer.reduce((sum, inv) => sum + inv.outstanding_amount, 0)
                    );
                    const maxCredit = Math.min(
                        centsToDollars(query.data.availableCredits),
                        customerOutstanding
                    );

                    initialCredits[customerId] = {
                        amount: maxCredit,
                        inputValue: maxCredit.toFixed(2),
                    };
                }
            });

            setSupplierCredits(initialCredits);
        }
    }, [modals.bulkManualCharge, areCreditsLoading, creditQueries.length]);

    useEffect(() => {
        const defaultMethod = paymentMethodsByCustomer[invoices[0]?.customer_id]?.payment_methods.filter((method) => method.is_primary)[0];
        if (isSameCustomerID && selectedPaymentMethod === null) {
            setSelectedPaymentMethod(defaultMethod);
        }
    }, [paymentMethodsByCustomer]);

    const getButtonText = ():string => {
        if (payInvoicesMutation.isLoading) return '';
        if (totalCreditToApply > 0 && total.toNumber() === 0) return 'Apply credit';
        if (totalCreditToApply > 0) return `Apply credit & charge ${getCurrencyFormat(total)}`;
        return `Charge ${getCurrencyFormat(total)}`;
    };

    if (!modals.bulkManualCharge) return null;

    return <Modal isOpen={modals.bulkManualCharge}
        title={
            <>{isSameCustomerID ? <><Typography className="m-1 pr-10" weight="font-semibold" variant="XL">Confirm charge(s)</Typography></>
                : <><Typography className="m-0 pr-10 mb-1" weight="font-semibold" variant="LG-2">Confirm charge for {getInvoicesWithPaymentMethod().length > 1 ? `${getInvoicesWithPaymentMethod().length} invoices` : 'invoice'} </Typography>
                    <Typography variant="LG-1" weight="font-regular" className="text-gray-600">The default payment method will be used for
                        each customer.</Typography></>

            }
            </>
        }
        headerPadding="mt-0 mb-5"
        titleSeparatorDesktop={true}
        close={handleOnClose}
        modalSize="SMALL"
        desktopModalWidth="lg:w-[480px]">
        <div className="p-6">
            {isIntegrationSetupNotComplete && <div className="pb-5">
                <RutterSetupNotCompleteBanner/>
            </div>}
            <div className="flex-col flex gap-0">
                {isSameCustomerID && <div className="flex flex-col w-full">
                    <div className="flex flex-row justify-between">
                        <Typography variant="LG-2" weight="font-medium" className="mb-2">{invoices[0]?.business_name}</Typography>
                        <Typography variant="LG-2" weight="font-medium" className="mb-2">{getCurrencyFormat(total)}</Typography>
                    </div>
                    <div className="flex flex-row justify-between mb-3.5">
                        <Typography variant="LG-1" weight="font-regular" className="text-gray-650">{invoices.length} invoice{invoices.length > 1 ? 's' : ''}</Typography>
                        <Typography variant="LG-1" weight="font-regular" className="text-gray-650">{formatAsCurrency(amount)}</Typography>
                    </div>

                    {/* Credit inputs for each customer */}
                    {!areCreditsLoading && creditQueries.map((query) => {
                        if (!query.data || query.data.availableCredits === 0) return null;

                        const { customerId } = query.data;
                        const invoicesForCustomer = invoicesByCustomerID[customerId] || [];
                        const customerOutstanding = centsToDollars(
                            invoicesForCustomer.reduce((sum, inv) => sum + inv.outstanding_amount, 0)
                        );
                        const maxCredit = Math.min(
                            centsToDollars(query.data.availableCredits),
                            customerOutstanding
                        );

                        const currentCredit = supplierCredits[customerId] || { amount: 0, inputValue: '0.00' };

                        return (
                            <CreditInput
                                key={customerId}
                                availableCredits={centsToDollars(query.data.availableCredits)}
                                maxApplicableCredits={maxCredit}
                                creditToApply={currentCredit.amount}
                                creditInputValue={currentCredit.inputValue}
                                onCreditChange={(value, inputValue) => {
                                    setSupplierCredits((prev) => ({
                                        ...prev,
                                        [customerId]: { amount: value, inputValue },
                                    }));
                                }} />
                        );
                    })}

                    {totalCreditToApply > 0 && (
                        <div className="flex flex-row justify-between mb-3.5">
                            <Typography variant="LG-1" weight="font-medium" className="text-gray-700">Total outstanding</Typography>
                            <Typography variant="LG-1" weight="font-medium" className="text-gray-700">{formatAsCurrency(amount - totalCreditToApply)}</Typography>
                        </div>
                    )}

                    {paymentMethodsByCustomer[invoices[0]?.customer_id]?.payment_methods?.length ? (
                        <div className="flex flex-row justify-between">
                            <Typography variant="BASE" weight="font-medium" className="text-gray-650 mb-2">Select payment method</Typography>
                            {isUsingSurcharges && <div className="flex flex-row gap-1">
                                <Typography variant="BASE" weight="font-medium" className="text-gray-650 mb-2">Surcharge</Typography>
                                <Tooltip show={showSurchargeTooltip}
                                    tooltipClassName="w-52 py-2.5 px-2.5"
                                    showArrow={false}
                                    placement="top-end"
                                    trigger={
                                        <div onMouseEnter={(): void => setShowSurchargeTooltip(true)} onMouseLeave={(): void => setShowSurchargeTooltip(false)}>
                                            <InfoOutlineIcon className="w-3 h-3 mb-px text-gray-600" />
                                        </div>}>
                                    <div>
                                        <span>A surcharge may be applied when payments are made using a credit card.</span>
                                    </div>
                                </Tooltip>
                            </div>}
                        </div>) : null}
                    <div className="mb-6 h-9.5">
                        {paymentMethodsByCustomer[invoices[0]?.customer_id]?.payment_methods?.length ? (
                            <div className="flex flex-col items-center justify-between gap-2">
                                {paymentMethodsByCustomer[invoices[0]?.customer_id]?.payment_methods.map((paymentMethod) => <RadioTile
                                    className="w-full border rounded items-center pt-1"
                                    key={paymentMethod.id}
                                    value={paymentMethod.id}
                                    onChange={(): void => {
                                        setSelectedPaymentMethod(paymentMethod);
                                    }}
                                    selected={paymentMethod === selectedPaymentMethod}>
                                    <div className="w-full flex flex-row justify-between items-center mx-2">
                                        <PaymentMethodBadge
                                            className="items-center my-0"
                                            key={paymentMethod.id}
                                            brand={paymentMethod.brand}
                                            last4={paymentMethod.last4}
                                            exp_month={paymentMethod.exp_month}
                                            exp_year={paymentMethod.exp_year}
                                            isDefault={paymentMethod.is_primary}
                                            mandate_status={paymentMethod.mandate_status} />
                                        {isUsingSurcharges && <Typography variant="LG-1" weight="font-regular" className="text-gray-650 m-0 mb-1">{`+${(formatAsCurrency(calculateSurchargeAfterCredits(paymentMethod, invoices.map((invoice) => invoice.estimated_surcharges), amount, Math.max(0, amount - totalCreditToApply))))}`}</Typography>}
                                    </div>
                                </RadioTile>)
                                }
                            </div>) : null}
                        <Button
                            onClick={() => setModal('addPaymentMethod', true, invoices[0])}
                            size="SMALL"
                            variant="TEXT"
                            className="text-1 p-0 pt-2">
                            Add payment method
                        </Button>
                    </div>

                </div>}
                {!isSameCustomerID && !arePaymentMethodsLoading && <ul className="flex flex-col gap-5">
                    {customerInvoices.map((gt, index) => {
                        const ct = gt?.reduce((acc, invoice) => acc + invoice.outstanding_amount, 0) ?? 0;
                        const customerHasPaymentMethods = paymentMethodsByCustomer[gt[0]?.customer_id]?.payment_methods?.length;
                        const invoiceNumbers = gt?.map((invoice) => invoice.invoice_number) ?? [];
                        const customerId = gt[0]?.customer_id;

                        // Find credit query for this customer
                        const creditQuery = creditQueries.find((q) => q.data?.customerId === customerId);
                        const hasCredits = creditQuery?.data && creditQuery.data.availableCredits > 0;
                        const customerOutstanding = centsToDollars(ct);
                        const maxCredit = hasCredits
                            ? Math.min(centsToDollars(creditQuery.data.availableCredits), customerOutstanding)
                            : 0;
                        const currentCredit = supplierCredits[customerId] || { amount: 0, inputValue: '0.00' };

                        return <li key={`${index}_${gt[0]?.id ?? ''}`}>
                            <div className="flex flex-col gap-5">
                                <div className="flex justify-between gap-2">
                                    <div className="space-y-1">
                                        <Typography as="div" weight="font-medium" variant="LG-1" className="text-gray-700">
                                            {gt[0]?.business_name}
                                        </Typography>
                                        <Typography as="div" weight="font-regular" variant="LG-1" className="text-gray-600">
                                            {invoiceNumbers?.join(', ')}
                                        </Typography>
                                    </div>
                                    <Typography as="div"
                                        weight="font-regular"
                                        variant="LG-1"
                                        className={mergeClassNames('text-gray-700', {
                                            'text-gray-500': !customerHasPaymentMethods,
                                        })}>
                                        {formatAsCurrency(centsToDollars(ct))}
                                    </Typography>
                                </div>

                                {/* Credit input for this customer if credits available */}
                                {hasCredits && (
                                    <CreditInput
                                        availableCredits={centsToDollars(creditQuery.data.availableCredits)}
                                        maxApplicableCredits={maxCredit}
                                        creditToApply={currentCredit.amount}
                                        creditInputValue={currentCredit.inputValue}
                                        onCreditChange={(value, inputValue) => {
                                            setSupplierCredits((prev) => ({
                                                ...prev,
                                                [customerId]: { amount: value, inputValue },
                                            }));
                                        }} />
                                )}

                                {/* Total outstanding after credit applied */}
                                {currentCredit.amount > 0 && (
                                    <div className="flex flex-row justify-between">
                                        <Typography variant="LG-1" weight="font-medium" className="text-gray-700">Total outstanding</Typography>
                                        <Typography variant="LG-1" weight="font-medium" className="text-gray-700">
                                            {formatAsCurrency(customerOutstanding - currentCredit.amount)}
                                        </Typography>
                                    </div>
                                )}

                                {!customerHasPaymentMethods && !arePaymentMethodsLoading
                                    && <Badge variant="ORANGE" className="py-2 px-3">
                                        <div className="flex items-center gap-3">
                                            <InfoIcon className="h-4 w-4 shrink-0"/>
                                            <Typography className="text-gray-700">This customer has no default payment method on
                                            file and will not be charged</Typography>
                                        </div>
                                    </Badge>}
                                <Separator/>
                            </div>
                        </li>;
                    })}
                </ul>}
                {!isSameCustomerID && arePaymentMethodsLoading && <Loading isDark/>}

                {invoices.length > 1 && <div>
                    <Checkbox
                        className="items-start [&>*:first-child]:shrink-0 items-center"
                        label={isSameCustomerID ? 'Lump invoice payments into 1 charge' : 'Charge multiple invoices as one payment for each customer'}
                        value={'isLump'}
                        checked={isLump}
                        onChange={():void => {
                            setIsLump(!isLump);
                        }}/>
                </div>}
            </div>
        </div>
        {hasIncompletePaymentMethod && <LoadingPaymentMethodBanner/>}
        <Separator/>
        <div className="pt-5 px-5 flex justify-end gap-3">
            <Button variant="TERTIARY_FILLED"
                size="SMALL"
                onClick={handleOnClose}>
                <Typography as="span" weight="font-medium">
                    Cancel
                </Typography>
            </Button>
            <Button variant="SECONDARY"
                disabled={payInvoicesMutation.isLoading || (!selectedPaymentMethod && isSameCustomerID) || (!isSameCustomerID && getInvoicesWithPaymentMethod().length === 0) || arePaymentMethodsLoading || hasIncompletePaymentMethod || isPendingMandatePaymentMethod(selectedPaymentMethod)}
                loading={payInvoicesMutation.isLoading}
                size="SMALL"
                onClick={handleCharge}>
                <Typography as="span" weight="font-medium">
                    {payInvoicesMutation.isLoading ? <Loading/> : getButtonText()}
                </Typography>
            </Button>
        </div>
    </Modal>;
};
